import { ChevronDownIcon } from '@/assets/icons/chevron-down.icon';
import { ExternalLinkIcon } from '@/assets/icons/external-link.icon';
import { HelpIcon } from '@/assets/icons/help.icon';
import { LogoutIcon } from '@/assets/icons/logout.icon';
import { StarIcon } from '@/assets/icons/star.icon';
import Button from '@/core/design-system/Button';
import { cn } from '@/core/utils/cn';
import { removeHttpProtocol } from '@/core/utils/remove-http-protocol';
import NoltWidget from '@/core/widgets/Nolt.widget';
import useAuth from '@/modules/auth/hooks/useAuth.hook';
import Settings from '@/modules/playground/components/settings';
import { PlaygroundMode } from '@/modules/playground/enums/mode.enum';
import usePlayground from '@/modules/playground/hooks/usePlayground.hook';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';

const Header = () => {
  const { state, handleSubmit, changeMode, openPreview, isSubmitting } = usePlayground();
  const { state: authState, logout } = useAuth();
  const navigate = useNavigate();

  const goToAnalytics = () => {
    navigate('/analytics');
  };

  const goToHelpCenter = () => {
    window.open('https://linkie.bio/help/en/', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex items-center justify-between flex-col md:flex-row py-8 px-24 z-50">
      <Menu as="div" className="relative inline-block text-left">
        <div>
          <MenuButton className="inline-flex w-full justify-center gap-x-2 text-sm font-semibold text-gray-900">
            <p className="text-light font-bold cursor-pointer">
              {removeHttpProtocol(process.env.HOME_URL ?? '')}/
              <span className="text-white">{state.payload.username}</span>
            </p>
            <ChevronDownIcon className="-mr-1 h-5 w-5 text-[#525760]" />
          </MenuButton>
        </div>

        <MenuItems
          unmount={false}
          transition
          className="px-2 absolute mt-2 w-56 origin-top-right rounded-lg bg-main ring-1 ring-secondary transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
        >
          <div className="py-1">
            <MenuItem>
              <a
                href="#"
                className="flex items-center justify-between px-2 py-4 text-sm text-lighter font-bold"
                onClick={openPreview}
              >
                <span>/{state.payload.username}</span>
                <ExternalLinkIcon />
              </a>
            </MenuItem>
            {authState.isAuthenticated ? (
              <div className="border-t border-secondary border-t-1">
                <MenuItem>
                  {({ close }) => (
                    <div className="mt-3">
                      <Settings closeMenu={close} />
                    </div>
                  )}
                </MenuItem>
              </div>
            ) : null}
            <MenuItem>
              <button
                type="button"
                onClick={goToHelpCenter}
                className="text-left flex items-center justify-between gap-x-2 mt-1 mb-2 py-2 px-3 rounded-lg w-full bg-transparent border border-secondary border-1 text-light font-bold text-sm hover:bg-dark transition-all duration-200 ease-in-out"
              >
                Help Center
                <HelpIcon />
              </button>
            </MenuItem>
            <MenuItem>
              <NoltWidget>
                <button
                  type="button"
                  className="nolt-button text-left flex items-center justify-between gap-x-2 mb-2 py-2 px-3 rounded-lg w-full bg-transparent border border-secondary border-1 text-light font-bold text-sm hover:bg-dark transition-all duration-200 ease-in-out"
                >
                  Leave Feedback
                  <StarIcon />
                </button>
              </NoltWidget>
            </MenuItem>
            {authState.isAuthenticated ? (
              <MenuItem>
                <button
                  type="button"
                  onClick={logout}
                  className="text-left flex items-center justify-between gap-x-2 mt-1 mb-2 py-2 px-3 rounded-lg w-full bg-dark border border-secondary border-1 text-light font-bold text-sm hover:bg-secondary transition-all duration-200 ease-in-out"
                >
                  Log Out
                  <LogoutIcon />
                </button>
              </MenuItem>
            ) : null}
          </div>
        </MenuItems>
      </Menu>

      <div
        className={cn(
          'flex md:grid gap-4 justify-center items-center mt-4 md:mt-0',
          authState.isAuthenticated ? 'grid-cols-3' : 'grid-cols-2',
        )}
      >
        {authState.isAuthenticated ? (
          <Button type="button" variant="outlined" color="secondary" onClick={goToAnalytics}>
            Analytics
          </Button>
        ) : null}
        <Button type="button" variant="outlined" color="secondary" onClick={changeMode}>
          {PlaygroundMode.EDITOR === state.mode ? 'Preview' : 'Edit'}
        </Button>
        <Button
          type="button"
          className="md:w-20"
          onClick={handleSubmit}
          disabled={state.actions.length === 0 || isSubmitting}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default Header;
